import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setSnackBar } from '../../../../../states/app/staticSlice.js';
import useAutoAddCustomerReceiver from '../../../../editor/hooks/useAutoAddRecievers';
import { useLoadEditorContent } from '../../../../editor/hooks/useLoadEditorContent';
import { useSetAttachements } from '../../../../editor/hooks/useSetAttachements';
import { useSetDraft } from '../../../../editor/hooks/useSetDraft';
import MessageTypeSelector from '../../../../editor/MessageTypeSelector';
import { ProcessTicketModal } from '../../../../editor/ProcessTicketModal';
import RichTextEditor from '../../../../editor/RichTextEditor';
import { HANDLE_MESSAGES_REQUEST } from '../../../../editor/utils';
import MessageRecipientForm from './MessageRecipientForm.jsx';

const NewMessage = (props) => {
  const { ticket, mutate, mutateTicket, ticketValidating } = props;
  const profile = useSelector((state) => state?.login?.profile);

  // Déclaration des états
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState(null);
  const [isMessageBoxHidden, setIsMessageBoxHidden] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [inlineAttachments, setInlineAttachments] = useState([]);
  const [receiverList, setReceiverList] = useState([]);
  const [receiverMP, setReceiverMP] = useState(null);
  const [errorOnEmailValidation, setErrorOnEmailValidation] = useState(null);
  const [isEmailModifiedByUser, setIsEmailModifiedByUser] = useState(false);
  const [openUpdateTicketStatus, setOpenUpdateTicketStatus] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingSend, setIsLoadingSend] = useState(false);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [recipientType, setRecipientType] = useState(null);
  const [_isEditorDirty, setIsEditorDirty] = useState(false);
  const dispatch = useDispatch();

  // Suivi de l'initiation du processus de brouillon pour chaque type de destinataire.
  const draftProcessInitiatedRef = useRef({});

  // Déclaration des hooks personnalisés
  const { isDraftSet, draft } = useSetDraft({
    ticket,
    recipientType,
    ticketValidating,
  });

  const { _isAttachmentSet, handleAttachmentUpdate } = useSetAttachements({
    isDraftSet,
    draft,
    ticket,
    recipientType,
    ticketValidating,
    attachments,
    setAttachments,
    inlineAttachments,
    setInlineAttachments,
  });

  const { _isRecieverSet, addReceiver } = useAutoAddCustomerReceiver({
    ticket,
    recipientType,
    receiverList,
    draft,
    isEmailModifiedByUser,
    setErrorOnEmailValidation,
    setIsEmailModifiedByUser,
    setEmail,
    setReceiverList,
    isDraftSet,
    setReceiverMP,
    isMessageBoxHidden,
    setSubject,
    subject,
  });

  const { isEditorContentLoaded } = useLoadEditorContent({
    draft,
    setMessage,
    isDraftSet,
    recipientType,
  });

  // Vide tous les champs du formulaire.
  const emptyFormFields = () => {
    setSubject('');
    setEmail('');
    setReceiverList([]);
    setReceiverMP(null);
    setAttachments([]);
    setMessage(null);
    setIsEditorDirty(false);
  };

  // Ferme le composant et réinitialise les états.
  const handleCloseMessageBox = () => {
    mutateTicket(undefined, { revalidate: true });
    mutate();
    emptyFormFields();
    setRecipientType(null);
    setIsLoadingSend(false);
    setIsLoadingSave(false);
    setIsMessageBoxHidden(true);
    setOpenUpdateTicketStatus(false);
    emptyFormFields();
  };

  // Envoie le message
  const handleSendMessage = async ({
    isTicketProcessedByUser = null,
    messageToSend = null,
  }) => {
    if (!draft?._id) {
      console.error("[NewMessage][handleSendMessage] Pas d'ID de brouillon.");
      throw new Error('No draft id');
    }

    if (isTicketProcessedByUser === null) {
      if (ticket?.status?.code !== 'PROCESSED' || !ticket?.hasActiveTask) {
        setOpenUpdateTicketStatus(true);
        return;
      }
    }

    const content = messageToSend || message;

    const messageParams = {
      userId: profile?._id,
      ticketId: ticket?._id,
      subject: subject,
      content,
      receivers: receiverList,
      recipientType: recipientType,
    };
    const draftId = draft?._id;

    setIsLoadingSend(true);
    await HANDLE_MESSAGES_REQUEST.sendMessage({
      messageParams,
      isTicketProcessed: isTicketProcessedByUser,
      draftId,
      handleCloseMessageBox,
    });
  };

  // Affiche la boîte de message pour un nouveau type de destinataire.
  const handleShowMessageBox = (selectedRecipientType) => {
    if (selectedRecipientType !== recipientType) {
      emptyFormFields();
    }
    setRecipientType(selectedRecipientType);
    setIsLoadingSave(false);
    setIsLoadingSend(false);
  };

  // Ajoute une pièce jointe.
  const handleFileAddEvent = async (fileAttachment) => {
    if (!draft?._id) return;

    await handleAttachmentUpdate(async () => {
      const loadingAttachment = { ...fileAttachment, status: 'loading' };
      console.log(
        '[handleFileAddEvent] setAttachments avec:',
        loadingAttachment
      );
      setAttachments((prev) => [...prev, loadingAttachment]);

      const result = await HANDLE_MESSAGES_REQUEST.createAttachment({
        attachments,
        draftId: draft._id,
        attachmentData: fileAttachment,
        mutateTicket,
        mutate,
        setIsLoadingSend,
        setIsLoadingSave,
      });

      const { error, isDuplicateAttachment } = result || {};

      if (error) {
        console.error('❌ [NewMessage][handleFileAddEvent] Erreur API:', error);
        console.log(
          '[handleFileAddEvent] setAttachments (error) avec:',
          attachments.filter(
            (att) =>
              (att?.file?.name || att?.name) !==
              (fileAttachment?.file?.name || fileAttachment?.name)
          )
        );
        setAttachments((prev) =>
          prev.filter(
            (att) =>
              (att?.file?.name || att?.name) !==
              (fileAttachment?.file?.name || fileAttachment?.name)
          )
        );
        const messageError = `Erreur lors de l'enregistrement de la pièce jointe`;
        dispatch(
          setSnackBar({ open: true, message: messageError, color: 'error' })
        );
      } else if (isDuplicateAttachment) {
        const messageError = `Pièce jointe déjà enregistrée`;
        dispatch(
          setSnackBar({ open: true, message: messageError, color: 'warning' })
        );
      } else {
        console.log('[handleFileAddEvent] setAttachments (success) avec:', [
          ...attachments.filter(
            (att) =>
              (att?.file?.name || att?.name) !==
              (fileAttachment?.file?.name || fileAttachment?.name)
          ),
          { ...fileAttachment, status: 'loading' },
        ]);
        setAttachments((prev) => {
          const updatedAttachments = prev.filter(
            (att) =>
              (att?.file?.name || att?.name) !==
              (fileAttachment?.file?.name || fileAttachment?.name)
          );
          return [
            ...updatedAttachments,
            { ...fileAttachment, status: 'loading' },
          ];
        });
        const messageError = `Pièce jointe enregistrée avec succès`;
        dispatch(
          setSnackBar({ open: true, message: messageError, color: 'success' })
        );
      }
    });
  };

  // Supprime une pièce jointe.
  const deleteAttachment = (att) => {
    HANDLE_MESSAGES_REQUEST.deleteAttachment({
      draftId: draft?._id,
      attachementToDelete: att,
      mutateTicket,
      mutate,
      setIsLoadingSend,
      setIsLoadingSave,
    });
  };

  // Sauvegarde le brouillon du message.
  const handleSaveNewMessage = async (contentToSave) => {
    setIsLoadingSave(true);
    await HANDLE_MESSAGES_REQUEST.saveMessage({
      draft,
      profile,
      ticket,
      subject,
      content: contentToSave,
      receiverList,
      recipientType,
      mutateTicket,
      mutate,
      setIsLoadingSave,
    });
    setIsEditorDirty(false);
  };

  // Gère le changement de sujet.
  const handleSubjectChange = (event) => {
    setSubject(event?.target?.value);
  };

  // Gère le changement de destinataire email.
  const handleReceiverChange = (event) => {
    setEmail(event?.target?.value);
    setErrorOnEmailValidation(null);
    setIsEmailModifiedByUser(true);
  };

  // Gère la suppression d'un destinataire.
  const handleReceiverDelete = (receiver) => {
    setReceiverList(receiverList.filter((rec) => receiver !== rec));
  };

  // Gère le changement de destinataire pour les MP.
  const handleReceiverChangeFromMP = (e) => {
    setReceiverList([e.target.value]);
    setReceiverMP(e.target.value);
  };

  // Gère le processus de création/chargement du brouillon et l'ouverture/fermeture de la boîte de message.
  useEffect(() => {
    // Nettoyage/réinitialisation si le type de destinataire est null.
    if (recipientType === null) {
      if (!isMessageBoxHidden) {
        setIsMessageBoxHidden(true);
      }
      console.log('newMessage useEffect isDraftSetUpdated', isDraftSet);
      setIsDraftLoading(false);
      draftProcessInitiatedRef.current = {};
      return;
    }

    const expectedDraftInTicket =
      recipientType === 'CONTACT'
        ? ticket?.contactDraft
        : ticket?.serviceProviderDraft;
    const expectedDraftId = expectedDraftInTicket?._id;
    const currentDraftId = draft?._id;

    // Lance le processus de brouillon si nécessaire.
    const shouldInitiateProcess =
      !draftProcessInitiatedRef.current[recipientType] ||
      (expectedDraftId && currentDraftId !== expectedDraftId);

    if (shouldInitiateProcess) {
      setIsDraftLoading(true);
      draftProcessInitiatedRef.current[recipientType] = true;

      if (!expectedDraftInTicket) {
        HANDLE_MESSAGES_REQUEST.createNewMessageDraft({
          ticket,
          subject: '',
          receiverList: [],
          currentRecipientType: recipientType,
          mutateTicket,
        });
      }
      return; // Sortir tôt après l'initiation.
    }

    // Ouvre la boîte de message et désactive le chargement si toutes les conditions sont remplies.
    if (
      recipientType &&
      isDraftSet &&
      isEditorContentLoaded &&
      isMessageBoxHidden
    ) {
      setIsMessageBoxHidden(false);
      console.log('newMessage useEffect 2 isDraftSetUpdated', isDraftSet);
      setIsDraftLoading(false);
    } else if (
      isDraftLoading &&
      isDraftSet &&
      isEditorContentLoaded &&
      !isMessageBoxHidden
    ) {
      console.log('newMessage useEffect 3 isDraftSetUpdated', isDraftSet);
      setIsDraftLoading(false);
    }
  }, [
    recipientType,
    isDraftSet,
    draft,
    isEditorContentLoaded,
    isMessageBoxHidden,
    ticket?.contactDraft,
    ticket?.serviceProviderDraft,
    mutateTicket,
    ticket,
  ]);

  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: 'auto',
        maxWidth: 'auto',
        overflow: 'auto',
      }}
    >
      <ProcessTicketModal
        openUpdateTicketStatus={openUpdateTicketStatus}
        setOpenUpdateTicketStatus={setOpenUpdateTicketStatus}
        handleSendMessage={handleSendMessage}
        isLoadingSend={isLoadingSend}
        mutateTicket={mutateTicket}
      />
      {isMessageBoxHidden ? (
        <MessageTypeSelector
          ticket={ticket}
          isDraftLoading={isDraftLoading}
          isLoadingSave={isLoadingSave}
          isLoadingSend={isLoadingSend}
          setRecipientType={setRecipientType}
          handleShowMessageBox={handleShowMessageBox}
        />
      ) : (
        <Card
          sx={{
            height: '100%',
          }}
        >
          <CardContent
            sx={{
              height: '100%',
              margin: 0,
              padding: 0,
            }}
          >
            {isDraftLoading || !isEditorContentLoaded ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ padding: 2 }}>
                  <MessageRecipientForm
                    ticket={ticket}
                    recipientType={recipientType}
                    email={email}
                    errorOnEmailValidation={errorOnEmailValidation}
                    handleReceiverChange={handleReceiverChange}
                    addReceiver={addReceiver}
                    subject={subject}
                    handleSubjectChange={handleSubjectChange}
                    receiverList={receiverList}
                    handleReceiverDelete={handleReceiverDelete}
                    receiverMP={receiverMP}
                    handleReceiverChangeFromMP={handleReceiverChangeFromMP}
                    setReceiverList={setReceiverList}
                  />
                </Box>
                <FormControl
                  sx={{ width: '100%', height: 'calc(100% - 150px)' }}
                >
                  <RichTextEditor
                    loadingSave={isLoadingSave}
                    loadingSend={isLoadingSend}
                    mutateTicket={mutateTicket}
                    mutate={mutate}
                    setIsLoadingSend={setIsLoadingSend}
                    setIsLoadingSave={setIsLoadingSave}
                    ticket={ticket}
                    draft={draft}
                    recipientType={recipientType}
                    subject={subject}
                    setSubject={setSubject}
                    message={message}
                    setMessage={setMessage}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    onFileAdd={handleFileAddEvent}
                    deleteAttachment={deleteAttachment}
                    receiverList={receiverList}
                    handleCancelNewMessage={handleCloseMessageBox}
                    handleSaveNewMessage={handleSaveNewMessage}
                    handleSendMessage={handleSendMessage}
                    setIsEditorDirty={setIsEditorDirty}
                    inlineAttachments={inlineAttachments}
                    setInlineAttachments={setInlineAttachments}
                  />
                </FormControl>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default NewMessage;
